/**
 * Host Dashboard Modals - Custom UI components to replace system prompts
 * NO SYSTEM PROMPTS ALLOWED - All user input uses these modals
 */
import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Crown, User, Building2, Zap, Link, Edit3 } from "lucide-react";

/**
 * Create VIP List Modal
 */
export function CreateListModal({ open, onOpenChange, listName, setListName, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]" data-testid="create-list-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-yellow-500" />
            Create VIP List
          </DialogTitle>
          <DialogDescription>
            Create a list to organize VIP guests, sponsors, press, or other groups.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="list-name">List Name</Label>
            <Input
              id="list-name"
              placeholder="e.g., VIP Guests, Sponsors, Press"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              data-testid="list-name-input"
              className="mt-1"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={onSubmit}
            disabled={!listName.trim()}
            data-testid="create-list-submit"
          >
            Create List
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Add Person to List Modal
 */
export function AddListPersonModal({ 
  open, 
  onOpenChange, 
  personData, 
  setPersonData, 
  onSubmit,
  listName,
  hostName,
  hostOrg 
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]" data-testid="add-list-person-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-purple-500" />
            Add Person to {listName || "List"}
          </DialogTitle>
          <DialogDescription>
            Add a placeholder for a VIP guest. They'll receive a personalized invite link.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="person-name">Name *</Label>
            <Input
              id="person-name"
              placeholder="e.g., Jane Smith"
              value={personData.name}
              onChange={(e) => setPersonData({...personData, name: e.target.value})}
              data-testid="person-name-input"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="person-email">Email (optional)</Label>
            <Input
              id="person-email"
              type="email"
              placeholder="e.g., jane@company.com"
              value={personData.email}
              onChange={(e) => setPersonData({...personData, email: e.target.value})}
              data-testid="person-email-input"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="person-org">Host (optional)</Label>
            <Input
              id="person-org"
              placeholder="e.g., Acme Corp"
              value={personData.org_name}
              onChange={(e) => setPersonData({...personData, org_name: e.target.value})}
              data-testid="person-org-input"
              className="mt-1"
            />
          </div>
          
          {/* VIP Invite Info */}
          <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
            <p className="text-sm text-purple-800">
              <span className="font-medium">VIP Invite Link:</span> When shared, the invite will show:
            </p>
            <p className="text-xs text-purple-600 mt-1">
              "You've been invited by <strong>{hostName || "the host"}</strong>
              {hostOrg && <> from <strong>{hostOrg}</strong></>}"
            </p>
            <p className="text-xs text-gray-500 mt-2">
              The VIP tag will be added to their RSVP automatically.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={onSubmit}
            disabled={!personData.name.trim()}
            data-testid="add-person-submit"
          >
            Add Person
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Add Organization to List Modal
 */
export function AddListOrgModal({ 
  open, 
  onOpenChange, 
  orgData, 
  setOrgData, 
  onSubmit,
  listName,
  hostName,
  hostOrg 
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]" data-testid="add-list-org-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-500" />
            Add Host to {listName || "List"}
          </DialogTitle>
          <DialogDescription>
            Add a placeholder organization. The contact will receive a personalized invite.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="org-name">Host Name *</Label>
            <Input
              id="org-name"
              placeholder="e.g., Acme Corp"
              value={orgData.name}
              onChange={(e) => setOrgData({...orgData, name: e.target.value})}
              data-testid="org-name-input"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="contact-name">Contact Person Name</Label>
            <Input
              id="contact-name"
              placeholder="e.g., John Doe"
              value={orgData.contact_name}
              onChange={(e) => setOrgData({...orgData, contact_name: e.target.value})}
              data-testid="contact-name-input"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="contact-email">Contact Email</Label>
            <Input
              id="contact-email"
              type="email"
              placeholder="e.g., john@acme.com"
              value={orgData.contact_email}
              onChange={(e) => setOrgData({...orgData, contact_email: e.target.value})}
              data-testid="contact-email-input"
              className="mt-1"
            />
          </div>

          {/* VIP Invite Info */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-sm text-blue-800">
              <span className="font-medium">VIP Invite Link:</span> When shared, the invite will show:
            </p>
            <p className="text-xs text-blue-600 mt-1">
              "You've been invited by <strong>{hostName || "the host"}</strong>
              {hostOrg && <> from <strong>{hostOrg}</strong></>}"
            </p>
            <p className="text-xs text-gray-500 mt-2">
              The VIP tag will be added to their RSVP automatically.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={onSubmit}
            disabled={!orgData.name.trim()}
            data-testid="add-org-submit"
          >
            Add Host
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Add/Edit Tool Modal
 */
export function ToolModal({ 
  open, 
  onOpenChange, 
  toolData, 
  setToolData, 
  onSubmit,
  isEditing = false
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]" data-testid="tool-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-purple-500" />
            {isEditing ? "Edit Tool" : "Add Custom Tool"}
          </DialogTitle>
          <DialogDescription>
            {isEditing 
              ? "Update the tool details. Changes will sync to the Overview tab."
              : "Add a custom tool to your event's tech stack."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="tool-name">Tool Name *</Label>
            <Input
              id="tool-name"
              placeholder="e.g., Airtable, Figma, Slack"
              value={toolData.name}
              onChange={(e) => setToolData({...toolData, name: e.target.value})}
              data-testid="tool-name-input"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="tool-link">Link (URL)</Label>
            <Input
              id="tool-link"
              type="url"
              placeholder="e.g., https://airtable.com/your-base"
              value={toolData.link}
              onChange={(e) => setToolData({...toolData, link: e.target.value})}
              data-testid="tool-link-input"
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">This link will be clickable on the Overview tab</p>
          </div>
          <div>
            <Label htmlFor="tool-desc">Description</Label>
            <Textarea
              id="tool-desc"
              placeholder="e.g., Guest list management, Design assets, Team communication"
              value={toolData.description}
              onChange={(e) => setToolData({...toolData, description: e.target.value})}
              data-testid="tool-desc-input"
              className="mt-1"
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={onSubmit}
            disabled={!toolData.name.trim()}
            data-testid="tool-submit"
          >
            {isEditing ? "Save Changes" : "Add Tool"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Delete Confirmation Modal
 */
export function DeleteConfirmModal({ 
  open, 
  onOpenChange, 
  title,
  description,
  onConfirm
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="delete-confirm-modal">
        <AlertDialogHeader>
          <AlertDialogTitle>{title || "Are you sure?"}</AlertDialogTitle>
          <AlertDialogDescription>
            {description || "This action cannot be undone."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={onConfirm}
            className="bg-red-600 hover:bg-red-700"
            data-testid="delete-confirm-btn"
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default {
  CreateListModal,
  AddListPersonModal,
  AddListOrgModal,
  ToolModal,
  DeleteConfirmModal
};
